import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import searchProducts, {
  getProductSuggestions,
} from "../services/searchBarServices";

// Retrieve search query from sessionStorage on load
const storedSearchQuery = sessionStorage.getItem("searchQuery") || "";

// Async thunk for fetching products
export const fetchSearchResults = createAsyncThunk(
  "search/fetchSearchResults",
  async (
    {
      searchQuery,
      category_id,
      parent_category_id,
      page = 0,
      min_price,
      max_price,
      selectedCategory,
      selectedParentCategory,
      lang,
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const storedSearchQuery = state.search.searchQuery;

      // Determine currentSearchQuery
      let currentSearchQuery = searchQuery || storedSearchQuery || "";

      // ✅ Priority condition:
      // If category_id or parent_category_id exist → ignore searchQuery
      if (category_id || parent_category_id) {
        currentSearchQuery = "";
      }

      // Build payload dynamically
      const productData = {
        search: currentSearchQuery,
        page,
        limit: 12,
      };

      if (min_price) productData.min_price = min_price;
      if (max_price) productData.max_price = max_price;
      if (selectedCategory) productData.selected_category = selectedCategory;
      if (selectedParentCategory)
        productData.selected_parent_category = selectedParentCategory;
      if (category_id) productData.category_id = category_id;
      if (parent_category_id)
        productData.parent_category_id = parent_category_id;

      // Call API service
      const response = await searchProducts(productData, lang);

      if (!response?.data?.records) {
        throw new Error("Invalid response format from API");
      }

      return {
        products: response.data.records,
        total: response.data.total,
        searchQuery: currentSearchQuery,
        page,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong while fetching search results"
      );
    }
  }
);

export const fetchProductSuggestions = createAsyncThunk(
  "search/fetchProductSuggestions",
  async ({ search, lang }, { dispatch, rejectWithValue }) => {
    try {
      // Immediately clear before new request
      dispatch(clearSuggestions());

      if (!search || search.trim() === "") {
        return { suggestions: [] };
      }

      const payload = { search };
      const response = await getProductSuggestions(payload, lang);

      return {
        suggestions: response?.data?.[0]?.suggestions || [],
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to get suggestions"
      );
    }
  }
);

// Redux slice
const searchSlice = createSlice({
  name: "search",
  initialState: {
    searchResults: [],
    currentPage: 0,
    suggestions: [],
    showSuggestions: false,
    suggestionsLoading: false,
    suggestionsError: null,
    loading: false,
    error: null,
    searchQuery: storedSearchQuery,
    category_id: "",
    parent_category_id: "",
    min_price: "",
    max_price: "",
    isSearching: false, // NEW
  },
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      // sessionStorage.setItem("searchQuery", action.payload); // Store in sessionStorage
    },
    resetSearch: (state) => {
      state.searchResults = [];
      state.category_id = "";
      state.parent_category_id = "";
      state.currentPage = 0;
      state.error = null;
      state.searchQuery = "";
      state.min_price = "";
      state.max_price = ""; // Clear query
      //  sessionStorage.removeItem("searchQuery"); // Remove from sessionStorage
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
      state.showSuggestions = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSearchResults.pending, (state, action) => {
        state.loading = true;
        state.error = null;

        // If this is a new search (page 0), set isSearching true
        if (action.meta.arg.page === 0) {
          state.isSearching = true;
        }
      })
      .addCase(fetchSearchResults.fulfilled, (state, action) => {
        state.loading = false;

        state.searchResults =
          action.payload.page === 0
            ? action.payload.products
            : [...state.searchResults, ...action.payload.products];

        state.currentPage = action.payload.page;
        state.searchQuery = action.payload.searchQuery;
        state.category_id = action?.meta?.arg?.category_id;
        state.parent_category_id = action?.meta?.arg?.parent_category_id;
        state.min_price = action?.meta?.arg?.min_price;
        state.max_price = action?.meta?.arg?.max_price;
        state.total = action.payload.total;

        // Once fulfilled, reset isSearching
        if (action.payload.page === 0) {
          state.isSearching = false;
        }
      })
      .addCase(fetchSearchResults.rejected, (state, action) => {
        state.loading = false;
        // Reset isSearching on error
        state.isSearching = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchProductSuggestions.pending, (state) => {
        state.suggestionsLoading = true;
        state.showSuggestions = true;
      })
      .addCase(fetchProductSuggestions.fulfilled, (state, action) => {
        state.suggestionsLoading = false;

        // action.payload = { suggestions: [...] }
        state.suggestions = action.payload.suggestions;

        state.showSuggestions = true;
      })

      .addCase(fetchProductSuggestions.rejected, (state, action) => {
        state.suggestionsLoading = false;
        state.suggestionsError = action.payload;
        state.showSuggestions = false;
      });
  },
});

export const { setSearchQuery, resetSearch, clearSuggestions } =
  searchSlice.actions;
export default searchSlice.reducer;
